import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Bot, Swords, Wrench, Zap, MapPin, Mail, Instagram, Github,
  Calendar, Clock, Trophy, Users, ArrowRight, CheckCircle2, Cog,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG = {
  combat: "https://static.prod-images.emergentagent.com/jobs/29eb9361-6dbb-406a-8418-50997598fe3f/images/263e373baeb17d34ed607124588e92e94d89c1994d35684526d77df4b673faea.png",
  workshop: "https://static.prod-images.emergentagent.com/jobs/29eb9361-6dbb-406a-8418-50997598fe3f/images/41504a8a170817616dd085cc452fd0769dcd90c21b1f1f3b667d5e23727ef943.png",
  mascot: "https://static.prod-images.emergentagent.com/jobs/29eb9361-6dbb-406a-8418-50997598fe3f/images/a62e0c79cda540293186d5a8b44974557b9baa3ee4962adbdbac4bd22dc5d3cf.png",
};

const GALLERY = [
  { url: "https://images.pexels.com/photos/7869084/pexels-photo-7869084.jpeg", alt: "Kids building a robotics project", span: "col-span-2 row-span-2" },
  { url: "https://images.unsplash.com/photo-1527430253228-e93688616381", alt: "Quirky blue toy robot", span: "" },
  { url: "https://images.pexels.com/photos/7868885/pexels-photo-7868885.jpeg", alt: "Hands-on robot workshop", span: "" },
  { url: "https://images.unsplash.com/photo-1576344671262-e7d3697ac226", alt: "Maker space sign", span: "" },
  { url: "https://images.pexels.com/photos/33827091/pexels-photo-33827091.jpeg", alt: "Pixel art robot", span: "" },
];

const Tag = ({ children, className = "" }) => (
  <span className={`inline-block bg-[#D4FF00] text-[#111] px-3 py-1 text-xs sm:text-sm font-mono-accent font-semibold uppercase tracking-wider border-2 border-[#111] nb-shadow-sm ${className}`}>
    {children}
  </span>
);

const stagger = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Nav() {
  const links = [
    ["Events", "#events"],
    ["About", "#about"],
    ["Gallery", "#gallery"],
    ["RSVP", "#rsvp"],
  ];
  return (
    <header className="sticky top-0 z-50 flex justify-between items-center px-5 sm:px-8 py-4 border-b-4 border-[#111] bg-[#FDFCF4]" data-testid="main-nav">
      <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
        <span className="bg-[#FF4B1F] border-2 border-[#111] p-1.5 nb-shadow-sm group-hover:rotate-12 transition-transform">
          <Bot strokeWidth={2.8} className="w-6 h-6 text-white" />
        </span>
        <span className="font-display font-black uppercase tracking-tighter text-lg sm:text-xl leading-none">
          Rose City<br className="hidden sm:block" /> Bot Brigade
        </span>
      </a>
      <nav className="hidden md:flex items-center gap-6 font-mono-accent uppercase text-sm tracking-wider">
        {links.map(([label, href]) => (
          <a key={href} href={href} className="hover:text-[#FF4B1F] hover:-translate-y-0.5 transition-all" data-testid={`nav-link-${label.toLowerCase()}`}>
            {label}
          </a>
        ))}
      </nav>
      <a href="#rsvp" className="nb-btn bg-[#FF4B1F] text-white border-4 border-[#111] nb-shadow px-4 py-2 font-display font-black uppercase text-sm" data-testid="nav-join-btn">
        Join Us
      </a>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-5 sm:px-12 py-12 sm:py-16 items-center relative overflow-hidden">
      <div className="absolute -top-10 right-1/4 opacity-10 hidden lg:block">
        <Cog className="w-40 h-40 animate-spinslow" strokeWidth={1.5} />
      </div>
      <motion.div initial="hidden" animate="show" variants={stagger} className="lg:col-span-7 z-10">
        <motion.div variants={stagger} custom={0}>
          <Tag>📍 Portland, Oregon · Est. 2026</Tag>
        </motion.div>
        <motion.h1 variants={stagger} custom={1} className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mt-5">
          Build bots.<br />
          <span className="text-[#FF4B1F]">Battle</span> bots.<br />
          <span className="bg-[#4361EE] text-white px-2 inline-block -rotate-1">Repeat.</span>
        </motion.h1>
        <motion.p variants={stagger} custom={2} className="mt-6 text-lg sm:text-xl max-w-xl font-medium leading-relaxed">
          We're Portland's loudest, sparkiest crew of robot builders and combat-bot fanatics. Bring an idea, leave with a machine. No experience required — just bring the chaos.
        </motion.p>
        <motion.div variants={stagger} custom={3} className="mt-8 flex flex-wrap gap-4">
          <a href="#events" className="nb-btn bg-[#D4FF00] text-[#111] border-4 border-[#111] nb-shadow px-6 py-3 font-display font-black uppercase flex items-center gap-2" data-testid="hero-events-btn">
            See The Events <ArrowRight strokeWidth={3} className="w-5 h-5" />
          </a>
          <a href="#rsvp" className="nb-btn bg-white text-[#111] border-4 border-[#111] nb-shadow px-6 py-3 font-display font-black uppercase" data-testid="hero-rsvp-btn">
            Reserve A Spot
          </a>
        </motion.div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-5 flex justify-center z-10">
        <img src={IMG.combat} alt="Combat robot mascot" className="w-full max-w-md border-4 border-[#111] nb-shadow-lg bg-[#2b2d36] animate-floaty" data-testid="hero-image" />
      </motion.div>
    </section>
  );
}

function Ticker() {
  const items = ["⚡ COMBAT BOT SESSION — JULY 25", "🔧 BUILD BOT WORKSHOP — JULY 11", "🏆 ALL SKILL LEVELS WELCOME", "🤖 TOOLS PROVIDED", "📍 PORTLAND OR"];
  return (
    <div className="bg-[#FF4B1F] text-white border-y-4 border-[#111] py-3" data-testid="ticker">
      <Marquee speed={55} gradient={false}>
        {items.concat(items).map((t, i) => (
          <span key={i} className="font-mono-accent font-semibold uppercase text-base sm:text-xl mx-8 tracking-wider">{t}</span>
        ))}
      </Marquee>
    </div>
  );
}

function EventCard({ tagColor, bg, fg, icon: Icon, date, title, time, location, desc, points, onRsvp, testid }) {
  return (
    <motion.div
      initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
      className={`relative border-4 border-[#111] nb-shadow p-6 sm:p-8 ${bg} ${fg} overflow-hidden`}
      data-testid={testid}
    >
      <div className="absolute -right-8 -top-8 opacity-15">
        <Icon className="w-44 h-44" strokeWidth={2} />
      </div>
      <div className="relative z-10">
        <span className={`inline-block ${tagColor} px-3 py-1 text-sm font-mono-accent font-bold uppercase tracking-wider border-2 border-[#111] nb-shadow-sm`}>
          {date}
        </span>
        <h3 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-4 leading-tight">{title}</h3>
        <div className="flex flex-wrap gap-4 mt-4 font-mono-accent text-sm uppercase">
          <span className="flex items-center gap-1.5"><Clock strokeWidth={2.5} className="w-4 h-4" /> {time}</span>
          <span className="flex items-center gap-1.5"><MapPin strokeWidth={2.5} className="w-4 h-4" /> {location}</span>
        </div>
        <p className="mt-4 text-base sm:text-lg font-medium leading-relaxed">{desc}</p>
        <ul className="mt-4 space-y-2">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-2 font-medium">
              <CheckCircle2 strokeWidth={2.5} className="w-5 h-5 mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
        <button onClick={onRsvp} className="nb-btn mt-6 bg-[#111] text-white border-4 border-[#111] nb-shadow px-6 py-3 font-display font-black uppercase flex items-center gap-2" data-testid={`${testid}-rsvp`}>
          RSVP Now <ArrowRight strokeWidth={3} className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}

function Events({ onRsvp }) {
  return (
    <section id="events" className="bg-[#E5E5E5] border-b-4 border-[#111] py-16 px-5 sm:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Zap className="w-7 h-7 text-[#FF4B1F]" strokeWidth={3} />
          <Tag>Upcoming Events</Tag>
        </div>
        <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-10">Two events. Zero chill.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <EventCard
            testid="event-workshop"
            bg="bg-[#4361EE]" fg="text-white" tagColor="bg-[#D4FF00] text-[#111]" icon={Wrench}
            date="📅 July 11" title="Build Bot Workshop" time="10AM – 4PM" location="PDX Makerspace"
            desc="A hands-on day of building. We'll guide you through wiring, motors, chassis and control — go home with a working bot."
            points={["Beginner-friendly, all ages 12+", "Tools & starter kits provided", "Mentors at every bench"]}
            onRsvp={() => onRsvp("Build Bot Workshop")}
          />
          <EventCard
            testid="event-combat"
            bg="bg-[#FF4B1F]" fg="text-white" tagColor="bg-[#D4FF00] text-[#111]" icon={Swords}
            date="📅 July 25" title="Combat Bot Session" time="1PM – 7PM" location="The Battle Arena, SE PDX"
            desc="Bring your bot to the arena. Brackets, bots, and bragging rights. Spectators welcome — bring snacks and safety glasses."
            points={["1lb & 3lb weight classes", "Polycarbonate arena, fully enclosed", "Trophies for top 3 builders"]}
            onRsvp={() => onRsvp("Combat Bot Session")}
          />
        </div>
      </div>
    </section>
  );
}

function About() {
  const stats = [
    { icon: Users, num: "120+", label: "Members" },
    { icon: Trophy, num: "40+", label: "Battles Fought" },
    { icon: Bot, num: "85", label: "Bots Built" },
  ];
  return (
    <section id="about" className="py-20 px-5 sm:px-12 relative overflow-hidden">
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-5 flex justify-center">
          <img src={IMG.mascot} alt="Friendly bot mascot waving" className="w-64 sm:w-72 animate-wobble" data-testid="about-mascot" />
        </motion.div>
        <div className="lg:col-span-7">
          <Tag>Who We Are</Tag>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-4 leading-tight">
            A garage gang for <span className="text-[#4361EE]">robot people.</span>
          </h2>
          <p className="mt-5 text-lg font-medium leading-relaxed">
            Rose City Bot Brigade started with a few welders, some hobby motors, and way too much coffee. Today we're a community of builders, students, engineers and total beginners who meet to design, build, and battle robots in Portland.
          </p>
          <p className="mt-4 text-lg font-medium leading-relaxed">
            Whether you want to win a combat bracket or just learn to solder, there's a bench with your name on it.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {stats.map((s, i) => (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} custom={i}
                className="bg-white border-4 border-[#111] nb-shadow-sm p-4 text-center" data-testid={`stat-${i}`}>
                <s.icon className="w-7 h-7 mx-auto text-[#FF4B1F]" strokeWidth={2.5} />
                <div className="font-display font-black text-2xl sm:text-3xl mt-2">{s.num}</div>
                <div className="font-mono-accent text-xs uppercase tracking-wider">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section id="gallery" className="py-16 px-5 sm:px-12 bg-[#111]">
      <div className="max-w-6xl mx-auto">
        <Tag>The Scrapyard</Tag>
        <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-4 mb-10 text-[#FDFCF4]">From the bench.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] sm:auto-rows-[200px] gap-4 sm:gap-6">
          {GALLERY.map((g, i) => (
            <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} custom={i}
              className={`overflow-hidden border-4 border-[#FDFCF4] nb-shadow ${g.span}`} data-testid={`gallery-item-${i}`}>
              <img src={g.url} alt={g.alt} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RsvpForm({ selectedEvent, setSelectedEvent }) {
  const [form, setForm] = useState({ name: "", email: "", experience: "first-timer", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const events = ["Build Bot Workshop", "Combat Bot Session"];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required, builder.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, { ...form, event: selectedEvent });
      setDone(true);
      toast.success("You're in! See you at the bench. 🤖");
      setForm({ name: "", email: "", experience: "first-timer", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something jammed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border-4 border-[#111] px-4 py-3 text-base font-mono-accent focus:outline-none focus:border-[#FF4B1F] transition-colors";

  return (
    <section id="rsvp" className="py-20 px-5 sm:px-8">
      <div className="max-w-2xl mx-auto bg-white border-4 border-[#111] nb-shadow-lg p-6 sm:p-10 relative" data-testid="rsvp-form-card">
        <span className="absolute -top-4 left-6 bg-[#D4FF00] border-2 border-[#111] px-3 py-1 font-mono-accent text-sm font-bold uppercase nb-shadow-sm">Save your spot</span>
        <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-2">Reserve a bench</h2>
        <p className="mt-3 font-medium text-lg">Free to join. We'll email you the details.</p>

        {done ? (
          <div className="mt-8 bg-[#D4FF00] border-4 border-[#111] nb-shadow p-6 text-center" data-testid="rsvp-success">
            <CheckCircle2 className="w-12 h-12 mx-auto" strokeWidth={2.5} />
            <p className="font-display font-black uppercase text-2xl mt-3">You're locked in!</p>
            <p className="font-medium mt-1">Check your inbox for the next steps.</p>
            <button onClick={() => setDone(false)} className="nb-btn mt-4 bg-[#111] text-white border-4 border-[#111] nb-shadow-sm px-5 py-2 font-display font-black uppercase text-sm" data-testid="rsvp-another-btn">
              RSVP for another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="rsvp-form">
            <div>
              <label className="font-mono-accent text-sm uppercase tracking-wider font-semibold block mb-2">Which event?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {events.map((ev) => (
                  <button type="button" key={ev} onClick={() => setSelectedEvent(ev)}
                    className={`nb-btn border-4 border-[#111] px-4 py-3 font-display font-black uppercase text-sm ${selectedEvent === ev ? "bg-[#FF4B1F] text-white" : "bg-white text-[#111]"}`}
                    data-testid={`rsvp-event-${ev === "Combat Bot Session" ? "combat" : "workshop"}`}>
                    {ev}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-accent text-sm uppercase tracking-wider font-semibold block mb-2">Name</label>
                <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ada Lovebot" data-testid="rsvp-name-input" />
              </div>
              <div>
                <label className="font-mono-accent text-sm uppercase tracking-wider font-semibold block mb-2">Email</label>
                <input className={inputCls} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="rsvp-email-input" />
              </div>
            </div>
            <div>
              <label className="font-mono-accent text-sm uppercase tracking-wider font-semibold block mb-2">Experience</label>
              <select className={inputCls} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="rsvp-experience-select">
                <option value="first-timer">First-timer</option>
                <option value="hobbyist">Hobbyist</option>
                <option value="seasoned-builder">Seasoned builder</option>
              </select>
            </div>
            <div>
              <label className="font-mono-accent text-sm uppercase tracking-wider font-semibold block mb-2">Anything we should know? (optional)</label>
              <textarea className={inputCls} rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="I'm bringing a 3lb spinner..." data-testid="rsvp-message-input" />
            </div>
            <button type="submit" disabled={submitting} className="nb-btn w-full bg-[#D4FF00] text-[#111] border-4 border-[#111] nb-shadow px-6 py-4 font-display font-black uppercase text-lg flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rsvp-submit-btn">
              {submitting ? "Sending..." : <>Count Me In <Zap strokeWidth={3} className="w-5 h-5" /></>}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#111] text-[#FDFCF4] py-16 px-5 sm:px-12" data-testid="footer">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-none text-center">
          Let's <span className="text-[#FF4B1F]">Build.</span>
        </h2>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mt-12 border-t-4 border-[#FDFCF4]/30 pt-8">
          <div className="flex items-center gap-2 font-display font-black uppercase">
            <Bot strokeWidth={2.8} className="w-6 h-6 text-[#D4FF00]" /> Rose City Bot Brigade
          </div>
          <div className="flex flex-col sm:flex-row gap-4 font-mono-accent text-sm uppercase tracking-wider items-center">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> Portland, OR</span>
            <a href="mailto:hello@rosecitybots.dev" className="flex items-center gap-1.5 hover:text-[#D4FF00]" data-testid="footer-email"><Mail className="w-4 h-4" /> hello@rosecitybots.dev</a>
          </div>
          <div className="flex gap-3">
            <a href="#top" className="bg-[#FDFCF4] text-[#111] border-2 border-[#FDFCF4] p-2 nb-btn" data-testid="footer-instagram"><Instagram className="w-5 h-5" /></a>
            <a href="#top" className="bg-[#FDFCF4] text-[#111] border-2 border-[#FDFCF4] p-2 nb-btn" data-testid="footer-github"><Github className="w-5 h-5" /></a>
          </div>
        </div>
        <p className="text-center font-mono-accent text-xs uppercase tracking-wider mt-8 text-[#FDFCF4]/50">
          © 2026 Rose City Bot Brigade · Built by humans & bots
        </p>
      </div>
    </footer>
  );
}

export default function BotCommunity() {
  const [selectedEvent, setSelectedEvent] = useState("Build Bot Workshop");

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  const goToRsvp = (event) => {
    setSelectedEvent(event);
    document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="App bg-[#FDFCF4] min-h-screen">
      <Toaster position="top-center" />
      <Nav />
      <Hero />
      <Ticker />
      <Events onRsvp={goToRsvp} />
      <About />
      <Gallery />
      <RsvpForm selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} />
      <Footer />
    </div>
  );
}
